import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import Image from 'next/image';
import NextLink from 'next/link';
import { A11y, Autoplay, EffectFade, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Button, Card, Flex, Heading, IconButton, LinkBox, LinkOverlay } from '@chakra-ui/react';
import { ChevronRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { trackEvent } from '~/features/analytics/track-event';
import { useActivePromoCampaign } from '~/features/promotions/hooks/use-active-promo-campaign';
import { useDelayedSwiperAutoplay } from '~/features/promotions/hooks/use-delayed-swiper-autoplay';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import { useExperimentalSegmentEvents } from '../hooks';
import { ChevronLeftIcon } from '../icons';
import type { CatalogCarouselItem } from '../models/catalog-carousel-item';
import { getDefaultCatalogCarouselItems } from '../utils/get-default-catalog-carousel-items';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export type { GetDefaultCatalogCarouselItemsOptions } from '../utils/get-default-catalog-carousel-items';
export type CarouselItem = CatalogCarouselItem;

type CatalogHeroSectionProps = {
  itemsOverride?: CarouselItem[];
  isAgenticAiEligibilityPending?: boolean;
  /** Insert the sitewide sale slide after this many value-prop slides (default 0 = before all). */
  summerSaleInsertIndex?: number;
};

export { getDefaultCatalogCarouselItems };

export function CatalogHeroSection({
  itemsOverride,
  isAgenticAiEligibilityPending,
  summerSaleInsertIndex,
}: CatalogHeroSectionProps = {}) {
  const { t } = useI18n();
  const heroTitle = t('catalog.hero.carousel.title');
  const { campaign, campaignContext, isGateReady, resolved, slideKey } = useActivePromoCampaign();

  const promoSlide =
    campaign?.surfaces.catalog && resolved != null
      ? campaign.surfaces.catalog({
          ...campaignContext,
          imagePriority: true,
          resolved,
        })
      : undefined;

  const replacedCarouselKeys = promoSlide ? campaign?.replacesEvergreenKeys?.catalog ?? [] : [];
  const allCarouselItems = (itemsOverride ?? getDefaultCatalogCarouselItems(t)).filter(
    (item) => !replacedCarouselKeys.includes(item.key)
  );

  return (
    <Flex
      as='section'
      bgGradient='linear(273.36deg, #021031 28.34%, #00298C 100%)'
      minHeight='292px'
      overflow='hidden'
      position='relative'
      padding={{
        base: '40px 0px 0px',
        md: '40px 0px',
      }}
    >
      <Box
        height='292px'
        inset={0}
        margin='0 auto'
        maxWidth='1250px'
        mixBlendMode='overlay'
        pointerEvents='none'
        position='absolute'
        display={{
          base: 'none',
          md: 'block',
        }}
      >
        <Image
          alt=''
          quality={75}
          src='/images/experiments/ab_plp_redesign_2025/udacity-u-bg.png'
          style={{
            objectFit: 'contain',
            objectPosition: 'right',
            transform: 'scaleX(-1)',
          }}
          fill
          priority
        />
      </Box>
      <ContentContainer
        display='flex'
        gap='40px'
        justifyContent='space-between'
        alignItems={{
          md: 'center',
        }}
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
      >
        <Heading
          as='h1'
          color='white'
          fontWeight='600'
          lineHeight='1.1'
          flex={{
            md: '0 1 470px',
          }}
          fontSize={{
            base: '2.5rem',
            md: '3.375rem',
          }}
          textAlign={{
            base: 'center',
            md: 'start',
          }}
          whiteSpace={{
            base: 'pre-wrap',
            md: 'unset',
          }}
        >
          {heroTitle}
        </Heading>
        <Box
          aria-hidden={isAgenticAiEligibilityPending ? true : undefined}
          opacity={isAgenticAiEligibilityPending ? 0 : 1}
          pointerEvents={isAgenticAiEligibilityPending ? 'none' : 'auto'}
          transition='opacity 200ms ease-out'
          flex={{
            md: '0 1 748px',
          }}
        >
          <ValuePropsCarousel
            isReady={isGateReady}
            items={allCarouselItems}
            promoSlide={promoSlide}
            promoSlideKey={slideKey ?? undefined}
            summerSaleInsertIndex={summerSaleInsertIndex}
            renderCard={({ item, index }, theme) => {
              const { cardBackgroundGradient, cardBorderColor, cardFrameBorderColor, titleEmphasisColor } = theme;
              const handleClick = () => trackEvent(item.eventPayload);
              const usesFigmaFrame = Boolean(cardFrameBorderColor);
              const isEntireCardClickable = Boolean(item.entireCardClickable);
              const linkAriaLabel = `${item.title.replace(/\*\*/g, '').replace(/\n/g, ' ')}: ${item.ctaLabel}`;

              const heading = (
                <HeadingWithEmphasis
                  text={item.title}
                  emphasisProps={{
                    color: titleEmphasisColor,
                  }}
                  headingProps={{
                    as: 'p',
                    fontWeight: '600',
                    fontSize: '1.375rem',
                    lineHeight: '1.2',
                    whiteSpace: {
                      md: 'pre-wrap',
                    },
                    color: 'white',
                    position: 'relative',
                  }}
                />
              );

              const ctaButtonProps = {
                variant: 'solid' as const,
                colorScheme: 'blue' as const,
                width: 'fit-content' as const,
                position: 'relative' as const,
                zIndex: 1,
                size: 'md' as const,
                alignSelf: {
                  base: 'flex-start' as const,
                  md: 'unset' as const,
                },
                marginTop: item.ctaMarginTopAuto ? { base: 'auto', md: undefined } : undefined,
              };

              const card = (
                <Card
                  as={isEntireCardClickable ? 'div' : 'article'}
                  bgGradient={cardBackgroundGradient}
                  border={usesFigmaFrame ? 'none' : '1px solid'}
                  borderColor={usesFigmaFrame ? undefined : cardBorderColor}
                  borderRadius='8px'
                  display='flex'
                  flex={1}
                  flexWrap='wrap'
                  gap={4}
                  isolation={isEntireCardClickable ? 'isolate' : undefined}
                  overflow='hidden'
                  position='relative'
                  alignItems={{
                    base: item.theme === 'claude-engineer' ? 'flex-start' : 'center',
                    md: 'center',
                  }}
                  flexDirection={{
                    base: 'column',
                    md: 'row',
                  }}
                  justifyContent={{
                    md: 'space-between',
                  }}
                  padding={{
                    base: 4,
                    md: 8,
                  }}
                >
                  <Box
                    inset={0}
                    position='absolute'
                    sx={{
                      img: {
                        objectFit: 'cover',
                        objectPosition: item.backgroundImageObjectPosition ?? 'bottom',
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    <Image
                      alt=''
                      priority={index === 0}
                      quality={item.backgroundImageQuality ?? 75}
                      src={item.backgroundImage}
                      fill
                    />
                  </Box>
                  {item.overlay && <Box bgColor='blackAlpha.600' inset={0} position='absolute' />}
                  <Flex
                    flexDirection='column'
                    gap={item.logoImage ? 2.5 : 0}
                    pointerEvents={isEntireCardClickable ? 'none' : undefined}
                    position='relative'
                    zIndex={isEntireCardClickable ? 1 : undefined}
                  >
                    {item.logoImage && (
                      <Box height='20px' position='relative' width='93px'>
                        <Image
                          alt='Claude'
                          src={item.logoImage}
                          style={{ objectFit: 'contain', objectPosition: 'left center' }}
                          fill
                        />
                      </Box>
                    )}
                    {isEntireCardClickable ? (
                      heading
                    ) : (
                      <LinkOverlay href={item.ctaUrl} onClick={handleClick}>
                        {heading}
                      </LinkOverlay>
                    )}
                  </Flex>
                  {isEntireCardClickable ? (
                    <Button {...ctaButtonProps} as='span' pointerEvents='none' tabIndex={-1} aria-hidden>
                      {item.ctaLabel}
                    </Button>
                  ) : (
                    <ButtonLink
                      linkProps={{ href: item.ctaUrl }}
                      buttonProps={{
                        ...ctaButtonProps,
                        onClick: handleClick,
                      }}
                    >
                      {item.ctaLabel}
                    </ButtonLink>
                  )}
                  {isEntireCardClickable ? (
                    <LinkOverlay
                      aria-label={linkAriaLabel}
                      as={NextLink}
                      href={item.ctaUrl}
                      inset={0}
                      position='absolute'
                      zIndex={2}
                      onClick={handleClick}
                    />
                  ) : null}
                </Card>
              );

              return (
                <Flex
                  border={usesFigmaFrame ? '1px solid' : '6px solid'}
                  borderColor={cardFrameBorderColor ?? 'hsla(0, 0%, 0%, 0.32)'}
                  borderRadius={usesFigmaFrame ? '8px' : '12px'}
                  height='full'
                  maxWidth='656px'
                  mx='auto'
                  width='100%'
                >
                  {isEntireCardClickable ? (
                    <LinkBox display='flex' flex={1} width='100%'>
                      {card}
                    </LinkBox>
                  ) : (
                    card
                  )}
                </Flex>
              );
            }}
          />
        </Box>
      </ContentContainer>
    </Flex>
  );
}

type ValuePropsCarouselCardThemes = Record<
  string,
  {
    cardBackgroundGradient: string;
    cardBorderColor: string;
    titleEmphasisColor: string;
    /** Replaces the default 6px dark carousel frame with a single border (Figma catalog banner). */
    cardFrameBorderColor?: string;
  }
>;
const THEME_STYLES: ValuePropsCarouselCardThemes = {
  blue: {
    cardBackgroundGradient: 'linear(91.92deg, #002375 0%, #031643 50%)',
    cardBorderColor: 'blue.500',
    titleEmphasisColor: 'blue.300',
  },
  purple: {
    cardBackgroundGradient: 'linear(91.92deg, #32005A 0%, #150026 50%)',
    cardBorderColor: 'purple.500',
    titleEmphasisColor: 'purple.300',
  },
  cyan: {
    cardBackgroundGradient: 'linear(92.29deg, #003747 0%, #00161C 42.09%)',
    cardBorderColor: 'cyan.500',
    titleEmphasisColor: 'cyan.300',
  },
  'mba-crimson': {
    cardBackgroundGradient: 'linear(92.29deg, #490914 0%, #190004 63%)',
    cardBorderColor: '#540916',
    titleEmphasisColor: '#FFA6B1',
  },
  'woolf-gold': {
    cardBackgroundGradient: 'linear(92.29deg, #221500 0%, #060A12 42.09%)',
    cardBorderColor: '#583404',
    titleEmphasisColor: '#FFD695',
  },
  'agentic-ai': {
    cardBackgroundGradient: 'linear(92.29deg, #000000 0%, #000000 100%)',
    cardBorderColor: '#763000',
    titleEmphasisColor: '#FFDCC3',
  },
  'claude-engineer': {
    cardBackgroundGradient: 'linear(92.29deg, #2A1610 0%, #000000 100%)',
    cardBorderColor: 'orange.300',
    cardFrameBorderColor: 'orange.300',
    titleEmphasisColor: 'orange.200',
  },
};

const CATALOG_AUTOPLAY_START_DELAY_MS = 5000;

function ValuePropsCarousel({
  items,
  renderCard,
  promoSlide,
  promoSlideKey,
  isReady = true,
  summerSaleInsertIndex = 0,
}: {
  items: CarouselItem[];
  renderCard: (
    { item, index }: { item: CarouselItem; index: number },
    theme: ValuePropsCarouselCardThemes[keyof ValuePropsCarouselCardThemes]
  ) => ReactNode;
  /** Prepended promo slide from the active campaign registry (e.g. sitewide sale banner). */
  promoSlide?: ReactNode;
  /** Stable key for analytics when a promo slide is prepended. */
  promoSlideKey?: string;
  /** Holds the carousel at `opacity: 0` until the promotion is fetched (anti-CLS for the prepended slide). */
  isReady?: boolean;
  /** Insert the sitewide sale slide after this many value-prop slides (default 0 = before all). */
  summerSaleInsertIndex?: number;
}) {
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';

  const bannerEventRef = useRef<Record<number, boolean>>({});
  const isClientReady = useClientRender();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const { i18n, isRtl } = useI18n();
  const promoActive = promoSlide != null && promoSlideKey != null;

  const cards = useMemo(() => {
    const valuePropSlides = items.map((item, index) => (
      <SwiperSlide key={item.title}>
        {renderCard({ item, index }, THEME_STYLES[item.theme] ?? THEME_STYLES.blue)}
      </SwiperSlide>
    ));

    if (!promoActive || !promoSlide || !promoSlideKey) {
      return valuePropSlides;
    }

    const promoSlideElement = (
      <SwiperSlide key={promoSlideKey}>
        {/* Match the sibling hero cards' width/centering (656px, centered) without their 6px Card frame. */}
        <Box height='full' maxWidth='656px' mx='auto' width='100%'>
          {promoSlide}
        </Box>
      </SwiperSlide>
    );

    return [
      ...valuePropSlides.slice(0, summerSaleInsertIndex),
      promoSlideElement,
      ...valuePropSlides.slice(summerSaleInsertIndex),
    ];
  }, [items, promoActive, promoSlide, promoSlideKey, renderCard, summerSaleInsertIndex]);

  // Slide order must mirror `cards` so `Banner Viewed` reports the right value at each realIndex.
  const slideKeys = useMemo(() => {
    const itemKeys = items.map((item) => item.key);

    if (!promoActive || !promoSlideKey) {
      return itemKeys;
    }

    return [...itemKeys.slice(0, summerSaleInsertIndex), promoSlideKey, ...itemKeys.slice(summerSaleInsertIndex)];
  }, [items, promoActive, promoSlideKey, summerSaleInsertIndex]);

  // Hold the carousel hidden until the promotion is fetched (router ready + query settled) so the
  // correct first slide — promo or default — paints first. We can't gate on `summer2026Active` alone:
  // it's false until the promo resolves, so the default slide would paint and then swap (anti-CLS).
  const isCarouselVisible = isClientReady && isReady;
  const { onDestroy: onSwiperDestroy, onSwiper } = useDelayedSwiperAutoplay({
    isEnabled: isCarouselVisible,
    startDelayMs: CATALOG_AUTOPLAY_START_DELAY_MS,
  });

  const handleBannerLoad = useCallback(
    (realIndex: number) => {
      // Don't record views while the carousel is still held hidden (opacity: 0) waiting on the promo
      // gate. Autoplay/loop init can otherwise fire `onRealIndexChange` for the pre-promo slide and
      // poison `bannerEventRef`, suppressing the real promo-slide view once the sitewide sale prepends a slide
      // and the swiper remounts.
      if (!isCarouselVisible) {
        return;
      }
      const hasFiredEvent = bannerEventRef.current[realIndex];
      if (isExperimentalTrackEventsEnabled && !hasFiredEvent) {
        bannerEventRef.current[realIndex] = true;
        trackEvent({
          name: 'Banner Viewed',
          type: 'view',
          label: 'hero_banner_viewed',
          value: slideKeys[realIndex],
        });
      }
    },
    [isCarouselVisible, isExperimentalTrackEventsEnabled, slideKeys]
  );

  // Emit the first `Banner Viewed` only once the carousel is visible (promo settled) so `slideKeys[0]`
  // reflects the prepended promo slide. Firing on mount would report the default slide's key and then
  // be blocked by `bannerEventRef` after the promo key is prepended, mis-tracking the promo view.
  useEffect(() => {
    if (!isCarouselVisible) return;
    handleBannerLoad(0);
  }, [handleBannerLoad, isCarouselVisible]);

  return (
    <Box position='relative'>
      <Box
        isolation='isolate'
        position='relative'
        // Reserve the mobile carousel height from first paint. The `.swiper` collapses to height:0
        // while hidden (so the pre-init width:1px layout never flashes), then expands to the active
        // slide height once the promo is fetched (~1s). Without this floor that 0→full expansion grew
        // the section and shifted everything below (CLS ~0.09). Desktop is absorbed by the section's
        // own min-height, so only mobile needs the reserve.
        minHeight={{
          base: '224px',
          md: 'unset',
        }}
        sx={{
          '.swiper': {
            opacity: isCarouselVisible ? 1 : 0,
            height: isCarouselVisible ? undefined : '0px',
            paddingBottom: {
              base: '40px',
              md: 'unset',
            },
          },
          '.swiper-slide': {
            height: 'auto',
          },
          '.swiper-pagination': {
            display: {
              base: 'block',
              md: 'none',
            },
            height: '30px',
          },
          '.swiper-pagination-bullet': {
            backgroundColor: 'blue.300',
            width: '16px',
            borderRadius: '8px',
            height: '4px',
            opacity: 0.33,
            transition: 'all 0.3s ease',
          },
          '.swiper-pagination-bullet-active': {
            backgroundColor: 'blue.300',
            opacity: 1,
            width: '56px',
            borderRadius: '8px',
            height: '4px',
          },
          // Note: this is a weird hack to make the swiper slide widths responsive.
          '.swiper-wrapper': {
            width: '1px',
          },
        }}
      >
        <Swiper
          key={isCarouselVisible ? 'ready' : 'pending'}
          direction='horizontal'
          effect='fade'
          modules={[A11y, Mousewheel, EffectFade, Autoplay, Navigation, Pagination]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }}
          fadeEffect={{
            crossFade: true,
          }}
          mousewheel={{
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 10,
          }}
          navigation={
            cards.length > 1
              ? {
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }
              : undefined
          }
          pagination={{
            clickable: true,
          }}
          loop
          onDestroy={onSwiperDestroy}
          onSwiper={onSwiper}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
          }}
          onRealIndexChange={(swiper) => {
            handleBannerLoad(swiper.realIndex);
          }}
        >
          {isClientReady ? cards : cards.slice(0, 1)}
        </Swiper>
      </Box>
      {cards.length > 1 && (
        <>
          <IconButton
            ref={prevRef}
            aria-label='Previous'
            color='grey.500'
            isDisabled={!isClientReady}
            left={0}
            opacity={0.5}
            position='absolute'
            size='sm'
            top='50%'
            transform='translateY(-50%)'
            variant='ghost'
            display={{
              base: 'none',
              md: 'inline-flex',
            }}
            icon={
              isRtl ? <ChevronRightIcon height='24px' width='24px' /> : <ChevronLeftIcon height='24px' width='24px' />
            }
          />
          <IconButton
            ref={nextRef}
            aria-label='Next'
            color='grey.500'
            isDisabled={!isClientReady}
            opacity={0.5}
            position='absolute'
            right={0}
            size='sm'
            top='50%'
            transform='translateY(-50%)'
            variant='ghost'
            display={{
              base: 'none',
              md: 'inline-flex',
            }}
            icon={
              isRtl ? <ChevronLeftIcon height='24px' width='24px' /> : <ChevronRightIcon height='24px' width='24px' />
            }
          />
        </>
      )}
    </Box>
  );
}
